import { DateTime, Duration } from "luxon";

import { createLogger } from "@/logger";

const log = createLogger("reset");

import { type DeletionResult, deleteItemsAndOrphanedContent } from "@/services/LocalContentDeletion";
import { idx, rowsByIndex, store } from "@/store/store";

const FEED_ITEM_PAGE_SIZE = 50;
const WARM_WINDOW = Duration.fromObject({ hours: 1 });
const COOL_WINDOW = Duration.fromObject({ hours: 24 });

export type EvictionResult = DeletionResult;

/**
 * Per-feed retention cap: recently-opened feeds keep everything, cool/subscribed feeds keep one
 * page, everything else keeps only the latest item. Items ranked past the cap (newest first) are
 * evicted along with any content no surviving item references.
 */
export async function applyFeedEviction(): Promise<EvictionResult> {
  const now = DateTime.now();
  const warmCutoff = now.minus(WARM_WINDOW).toJSDate().toISOString();
  const coolCutoff = now.minus(COOL_WINDOW).toJSDate().toISOString();

  const itemIdsToEvict: string[] = [];
  const affectedFeedIds = new Set<string>();

  for (const feed of store.getAll("feed").values()) {
    let cap: number | null;
    if (feed.lastOpened != null && feed.lastOpened >= warmCutoff) {
      cap = null;
    } else if (feed.lastOpened != null && feed.lastOpened >= coolCutoff) {
      cap = FEED_ITEM_PAGE_SIZE;
    } else {
      const subscribed = rowsByIndex("feedPreference", idx.feedPreferencesByFeed, feed.id).some(
        (preference) => preference.pushNotificationPreference !== "none",
      );
      cap = subscribed ? FEED_ITEM_PAGE_SIZE : 1;
    }
    if (cap === null) continue;

    const items = rowsByIndex("item", idx.itemsByFeed, feed.id);
    if (items.length <= cap) continue;
    items.sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));
    for (const item of items.slice(cap)) {
      itemIdsToEvict.push(item.id);
      affectedFeedIds.add(feed.id);
    }
  }

  if (itemIdsToEvict.length === 0) {
    log.debug("[applyFeedEviction] No items to evict");
    return { deletedItems: 0, deletedContentIds: 0, affectedFeeds: 0 };
  }

  return deleteItemsAndOrphanedContent(itemIdsToEvict, [...affectedFeedIds], "applyFeedEviction");
}
